<template>
  <div v-if="shouldShow" class="responsive_button">
    <router-link :to="{ name: routeName }" class="button-card-btn">
      <i :class="['fa-solid', computedIcon, 'card-icon']"></i>
      <span class="card-text">{{ label }}</span>
    </router-link>
  </div>
</template>

<script setup>
import {computed, defineProps} from 'vue'
import {piniaCompanySettingsStore} from "@/store/piniaCompanySettingsStore";

const piniaCompanySettingsState = piniaCompanySettingsStore();
const companySettings = computed(() => piniaCompanySettingsState.companySettings);

const companyColor = computed(() => companySettings.value?.colorCode || '#15803d');

const companyBgColor = computed(() => companySettings.value?.colorCode
    ? `${companySettings.value.colorCode}15`
    : '#ecfdf5'
)

const props = defineProps({
  enabled: {
    type: Boolean,
    required: true
  },
  currentUserRole: {
    type: String,
    required: false,
    default: null
  },
  // Made optional by removing 'required' and providing an empty array as default
  allowedRoles: {
    type: Array,
    required: false,
    default: () => []
  },
  routeName: {
    type: String,
    required: true
  },
  coachingType: {
    type: String,
    required: true,
    validator(value) {
      // The value must match one of these exact keys
      const allowedTypes = [
        'private',
        'group',
        'socioTherapy',
        'assistedLiving',
        'caseManagement',
        'privateLearning',
        'privateInquiry'
      ]
      return allowedTypes.includes(value)
    }
  },
  label: {
    type: String,
    required: true
  }
})

// Map that associates each coaching type with its corresponding Font Awesome icon
const iconMap = {
  private: 'fa-user-tie',
  group: 'fa-users',
  socioTherapy: 'fa-hand-holding-heart',
  assistedLiving: 'fa-house-chimney-user',
  caseManagement: 'fa-briefcase',
  privateLearning: 'fa-graduation-cap',
  privateInquiry: 'fa-user-clock'
}

// Compute the icon class based on the passed coaching type
const computedIcon = computed(() => {
  // Returns a fallback icon if an unknown type is provided
  return iconMap[props.coachingType] || 'fa-star'
})

// Logic to determine whether the button should be displayed
const shouldShow = computed(() => {
  // If the coaching setting is disabled, never show the button
  if (!props.enabled) return false

  // If no specific roles are required (allowedRoles is empty), show it immediately
  if (props.allowedRoles.length === 0) return true

  // If roles are specified, verify if the current user's role is included
  return props.allowedRoles.includes(props.currentUserRole)
})
</script>

<style scoped>

.responsive_button {
  width: 25%;
  padding-right: 10px;
  padding-bottom: 20px;
  float: left;
  box-sizing: border-box;
}

.button-card-btn {
  display: flex;
  align-items: center;
  min-height: 50px;
  padding: 5px 10px;
  background-color: v-bind(companyBgColor);
  border: 1px solid v-bind(companyColor);

  border-radius: 3px;
  text-decoration: none !important;
  color: #1e293b !important;
  font-weight: 500;
  font-size: 0.92rem;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.button-card-btn:hover {
  transform: translateY(-2px);
  border-color: v-bind(companyColor);
  background-color: v-bind(companyBgColor);
  filter: brightness(0.97);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  color: v-bind(companyColor) !important;
}

.button-card-btn:active {
  transform: translateY(0);
  filter: brightness(0.92);
}

.card-icon {
  font-size: 1.20rem;
  margin-right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  flex-shrink: 0;
  border-radius: 8px;
  transition: all 0.2s ease-in-out;
  box-sizing: border-box;

  color: v-bind(companyColor) !important;
  background-color: v-bind(companyBgColor) !important;
}

.button-card-btn:hover .card-icon {
  color: #ffffff !important;
  background-color: v-bind(companyColor) !important;
}

.card-text {
  flex-grow: 1;
  line-height: 1.3;
  word-break: break-word;
  text-align: left;
}

@media (max-width: 1460px) {
  .responsive_button {
    width: 33%;
  }

  .button-card-btn {
    font-size: 0.8rem;
  }

  .card-icon {
    font-size: 1rem;
    width: 32px;
    height: 32px;
  }
}

@media (max-width: 1200px) {
  .responsive_button {
    width: 50%;
  }
}

@media (max-width: 768px) {
  .responsive_button {
    width: 100% !important;
  }
}
</style>